import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { CreateEntityWidgetConfig } from '../config/create-entity-widget-config.types';
import { jemmaRequestContext, jemmaSecureBaseUrl } from '../http/jemma-request.util';
import { JemmaPaged, JemmaResponseModel } from '../organization/jemma-organization.types';
import { JemmaStudyCreateRequest, JemmaStudyCreateResponseData, JemmaStudyStatus } from './jemma-study.types';

const _PAGE_SIZE = 100;

/**
 * Minimal Jemma API client for the widget's Study tab.
 *
 * `getStudyStatuses()` fetches a single page of up to 100 results, mirroring
 * `JemmaOrganizationApiService.getOrganizationTypes()` — study statuses are a
 * small, fixed codeset that comfortably fits within one page.
 *
 * Stateless: every method takes the calling widget instance's config rather than injecting
 * it, so one service instance safely serves several `<wcg-create-entity>` elements with
 * different tokens at once.
 */
@Injectable({ providedIn: 'root' })
export class JemmaStudyApiService {
    private readonly _http = inject(HttpClient);

    getStudyStatuses(config: CreateEntityWidgetConfig): Observable<JemmaStudyStatus[]> {
        const _params = new HttpParams().set('page', 1).set('size', _PAGE_SIZE);
        return this._http
            .get<JemmaResponseModel<JemmaPaged<JemmaStudyStatus>>>(`${jemmaSecureBaseUrl(config)}/study-status`, {
                params: _params,
                context: jemmaRequestContext(config)
            })
            .pipe(map(response => response.data?.results ?? []));
    }

    createStudy(config: CreateEntityWidgetConfig, body: JemmaStudyCreateRequest): Observable<JemmaStudyCreateResponseData> {
        return this._http
            .post<JemmaResponseModel<JemmaStudyCreateResponseData>>(`${jemmaSecureBaseUrl(config)}/studies`, body, { context: jemmaRequestContext(config) })
            .pipe(
                map(response => {
                    if (!response.data) {
                        throw new Error('Jemma API returned no study data.');
                    }
                    return response.data;
                })
            );
    }
}
